import { supabase } from './supabase';
import { normalizeDate, type Mapping, type ParsedSource, type TargetField } from './importClients';
import type { TabRole } from './importHistory';
import type { Client } from '../store/types';

/* ============================================================
   aiImport — the AI side of the import wizard.

   buildDigest() condenses the upload to headers + a few sample
   rows per tab (never the whole sheet, hard byte budget); the
   ai-import edge function runs Haiku over it; sanitizeAi()
   distrusts everything that comes back — hallucinated headers,
   out-of-range tab ids, double-assigned columns all get dropped
   so the wizard can apply the result blindly.
============================================================ */

const MAX_TABS = 12;
const TARGET_BYTES = 10_000;
const MAX_CELL = 100;

export interface DigestTab {
  id: number;
  fileName: string;
  tabName?: string;
  headers: string[];
  rows: string[][];
  totalRows: number;
}
export interface Digest { tabs: DigestTab[]; }

export function buildDigest(sources: ParsedSource[]): Digest {
  const clip = (v: string) => (v.length > MAX_CELL ? v.slice(0, MAX_CELL) + '…' : v);
  const make = (rowsPerTab: number, cols: number): Digest => ({
    tabs: sources.slice(0, MAX_TABS).map((s, id) => ({
      id,
      fileName: s.fileName,
      tabName: s.tabName,
      headers: s.headers.slice(0, cols).map(clip),
      rows: s.rows.slice(0, rowsPerTab).map((r) => r.slice(0, cols).map((c) => clip(c || ''))),
      totalRows: s.rows.length,
    })),
  });
  // Shrink the sample until it fits the budget; the model needs shape, not bulk.
  for (const [rows, cols] of [[12, 40], [6, 40], [3, 20], [2, 12]] as const) {
    const d = make(rows, cols);
    if (JSON.stringify(d).length <= TARGET_BYTES) return d;
  }
  return make(1, 8);
}

export type AiOutcome =
  | { kind: 'roster'; tabs: number[]; mapping: Mapping; noteHeaders: string[]; reason: string }
  | { kind: 'client_log'; name: string; draft: Partial<Client>; tabRoles: Record<number, TabRole>; reason: string }
  | { kind: 'unknown'; reason: string }
  | { kind: 'limit' }
  | { kind: 'unconfigured' }
  | { kind: 'error' };

const FIELDS: TargetField[] = ['name', 'email', 'phone', 'startDate', 'goals', 'injuries', 'note'];
const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

export function sanitizeAi(raw: unknown, sources: ParsedSource[]): AiOutcome {
  const r = raw as Record<string, unknown> | null;
  if (!r || typeof r !== 'object') return { kind: 'error' };
  const reason = str(r.reason);

  if (r.kind === 'client_log') {
    const c = (r.client || {}) as Record<string, unknown>;
    const name = str(c.name);
    if (!name) return { kind: 'unknown', reason };
    const draft: Partial<Client> = { name };
    const email = str(c.email);
    if (email) draft.email = email;
    const phone = str(c.phone);
    if (phone) draft.phone = phone;
    const start = normalizeDate(str(c.startDate));
    if (start) draft.startDate = start;
    const goals = str(c.goals);
    if (goals) draft.goals = goals;
    const injuries = str(c.injuries);
    if (injuries) draft.injuries = injuries;
    // Per-tab role hints — the history extractor uses them as classification
    // overrides; only valid ids/roles survive.
    const VALID_ROLES = new Set<TabRole>(['workouts', 'checkins', 'profile', 'diet', 'skip']);
    const tabRoles: Record<number, TabRole> = {};
    for (const t of Array.isArray(r.tabRoles) ? r.tabRoles : []) {
      const id = Number((t as any)?.id);
      const role = (t as any)?.role;
      if (Number.isInteger(id) && id >= 0 && id < sources.length && VALID_ROLES.has(role)) {
        tabRoles[id] = role;
      }
    }
    return { kind: 'client_log', name, draft, tabRoles, reason };
  }

  if (r.kind === 'roster') {
    const tabs = (Array.isArray(r.tabs) ? r.tabs : [])
      .map((n) => Number(n))
      .filter((n) => Number.isInteger(n) && n >= 0 && n < Math.min(sources.length, MAX_TABS));
    const chosen = tabs.length ? tabs.map((i) => sources[i]) : sources;
    const headerSet = new Set(chosen.flatMap((s) => s.headers));
    const rawMap = (r.mapping || {}) as Record<string, unknown>;
    const mapping: Mapping = {};
    const used = new Set<string>();
    for (const f of FIELDS) {
      const h = rawMap[f];
      if (typeof h === 'string' && headerSet.has(h) && !used.has(h)) {
        mapping[f] = h;
        used.add(h);
      }
    }
    const noteHeaders = (Array.isArray(r.noteHeaders) ? r.noteHeaders : [])
      .filter((h): h is string => typeof h === 'string' && headerSet.has(h) && !used.has(h));
    return { kind: 'roster', tabs, mapping, noteHeaders, reason };
  }

  return { kind: 'unknown', reason };
}

export async function aiAnalyze(sources: ParsedSource[]): Promise<AiOutcome> {
  try {
    const { data, error } = await supabase.functions.invoke('ai-import', {
      body: { digest: buildDigest(sources) },
    });
    if (error || !data) return { kind: 'error' };
    if (!data.ok) {
      if (data.reason === 'limit') return { kind: 'limit' };
      if (data.reason === 'unconfigured') return { kind: 'unconfigured' };
      return { kind: 'error' };
    }
    return sanitizeAi(data.result, sources);
  } catch {
    return { kind: 'error' };
  }
}
